import React, { useCallback, useEffect, useMemo } from "react";
import { useFormik, FormikProvider, useFormikContext } from "formik";
import { Box, Button, Grid as GridBase, Typography } from "@mui/material";

import { MUICustomAutocompleteSelect } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import RpNumberFieldBase from "@/components/muiCustom/textField/RpNumberField";
import RpMuiSwitch from "@/components/muiCustom/switch/RpMuiSwitch";
import BulkSelectionLayout from "@/features/campaigns/components/bulkSelection/BulkSelectionLayout";
import {
  rrdDisplayBulkSelectionAppliedMsg,
  rrdDisplayFillAllFieldsMsg,
  rrdDisplaySelectLocationMsg,
} from "@/features/campaigns/direct-mail/rrd/components/bulk-selection/RRDBulkSelectionMsg";
import NationalPlusUpToolTips from "../NationalPlusUpToolTips";
import {
  calculateNationalPlusUpCosts,
  validateNationalPlusUpStoreCopyQuantity,
} from "../../utils/nationalPlusUpHelpers";
import {
  getRRDStoreCopyDefaultQuantity,
  getRRDStoreCopyFormFields,
  isRRDStoreCopyFixedQuantity,
  resolveRRDStoreCopyConfig,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdStoreCopyConfigHelper";
import {
  getNationalPlusUpBulkPrintTypeOptions,
  getNationalPlusUpBulkStoreCopyConfig,
  getNationalPlusUpMatchingBulkLocationIds,
  validateNationalPlusUpBulkSelection,
} from "./nationalPlusUpBulkSelectionHelpers";
import { doRRDPrintTypesMatch } from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";

type NationalPlusUpRecord = Record<string, any>;
type LocationId = string | number;

type NationalPlusUpBulkValues = {
  print_type: string;
  distribution: boolean;
  store_copy: boolean;
  store_copy_qnt: string | number;
};

type NationalPlusUpBulkSelectionProps = {
  onToggleBulkSelection?: (_isOpened: boolean) => void;
  checkedLocations?: LocationId[];
  locations?: NationalPlusUpRecord[];
  setDisableLocations: (_locationIds: LocationId[]) => void;
};

type NationalPlusUpBulkSelectionFormProps = {
  onSubmitBulkSelection: (
    _values: NationalPlusUpBulkValues
  ) => Promise<boolean>;
  onChangeBulkSelection?: (_values: NationalPlusUpBulkValues) => void;
  checkedLocations: LocationId[];
  locationValues: Record<string, NationalPlusUpRecord>;
};

const Grid = GridBase as React.ComponentType<Record<string, any>>;
const RpNumberField = RpNumberFieldBase as React.ComponentType<
  Record<string, any>
>;

const initialValues = {
  print_type: "",
  distribution: false,
  store_copy: false,
  store_copy_qnt: "",
} satisfies NationalPlusUpBulkValues;

function NationalPlusUpBulkSelection({
  onToggleBulkSelection,
  checkedLocations = [],
  locations = [],
  setDisableLocations,
}: NationalPlusUpBulkSelectionProps) {
  const { values: locationValues, setFieldValue } =
    useFormikContext<Record<string, NationalPlusUpRecord>>();

  const applyBulkSelection = useCallback(
    async (values: NationalPlusUpBulkValues) => {
      if (!checkedLocations?.length) {
        rrdDisplaySelectLocationMsg();
        return false;
      }

      const validationErrors = validateNationalPlusUpBulkSelection({
        values,
        checkedLocations,
        locationValues,
      });
      if (validationErrors.length) {
        rrdDisplayFillAllFieldsMsg(validationErrors);
        return false;
      }

      const matchingLocationIds = getNationalPlusUpMatchingBulkLocationIds({
        checkedLocations,
        locationValues,
        printType: values.print_type,
        storeCopy: Boolean(values.store_copy),
      });

      matchingLocationIds.forEach((locationId) => {
        const location = locationValues?.[String(locationId)];
        if (!location) return;

        const distribution =
          Boolean(values.distribution) &&
          Boolean(location.distribution_available);
        const storeCopy = Boolean(values.store_copy);
        const storeCopyConfig = resolveRRDStoreCopyConfig({ location });
        const storeCopyFields = getRRDStoreCopyFormFields(storeCopyConfig);
        const storeCopyQnt =
          storeCopy && getRRDStoreCopyDefaultQuantity(storeCopyConfig)
            ? getRRDStoreCopyDefaultQuantity(storeCopyConfig)
            : storeCopy
              ? values.store_copy_qnt
              : "";
        const costs = calculateNationalPlusUpCosts({
          distribution,
          plusUpCost: location.plus_up_cost_admin,
          storeCopy,
          storeCopyQnt,
          storeCopyCpm: location.store_copy_cpm,
          storeCopyConfig,
        });

        if (location.distribution_available) {
          setFieldValue(`${locationId}.distribution`, distribution);
        }
        setFieldValue(`${locationId}.plus_up_cost`, costs.plus_up_cost);
        setFieldValue(`${locationId}.store_copy`, storeCopy);
        setFieldValue(`${locationId}.store_copy_qnt`, storeCopyQnt);
        setFieldValue(`${locationId}.store_copy_cost`, costs.store_copy_cost);
        setFieldValue(
          `${locationId}.store_copy_cpm`,
          storeCopyFields.store_copy_cpm
        );
        setFieldValue(
          `${locationId}.store_copy_min_quantity`,
          storeCopyFields.store_copy_min_quantity
        );
        setFieldValue(
          `${locationId}.store_copy_max_quantity`,
          storeCopyFields.store_copy_max_quantity
        );
        setFieldValue(
          `${locationId}.store_copy_fixed_quantity`,
          storeCopyFields.store_copy_fixed_quantity
        );
        setFieldValue(`${locationId}.total_cost`, costs.total_cost);
      });

      await rrdDisplayBulkSelectionAppliedMsg();
      return true;
    },
    [checkedLocations, locationValues, setFieldValue]
  );

  const handleBulkSelectionChange = useCallback(
    (values: NationalPlusUpBulkValues) => {
      if (values.store_copy && values.print_type) {
        const disabledLocationIds = locations
          .filter((location) => {
            const locationValue = locationValues?.[String(location.id)];
            return !doRRDPrintTypesMatch(
              locationValue?.print_type,
              values.print_type
            );
          })
          .map((location) => location.id);

        setDisableLocations(disabledLocationIds);
        return;
      }

      if (!values.distribution || values.store_copy) {
        setDisableLocations([]);
        return;
      }

      const disabledLocationIds = locations
        .filter((location) => {
          const locationValue = locationValues?.[String(location.id)];
          return !locationValue?.distribution_available;
        })
        .map((location) => location.id);

      setDisableLocations(disabledLocationIds);
    },
    [locations, locationValues, setDisableLocations]
  );

  return (
    <BulkSelectionLayout
      collapsedHeading="Want to make bulk selections"
      expandedHeading="National Plus Up"
      expandedTitle="Make your selections in bulk"
      onToggleBulkSelection={onToggleBulkSelection}
    >
      <NationalPlusUpBulkSelectionForm
        onSubmitBulkSelection={applyBulkSelection}
        onChangeBulkSelection={handleBulkSelectionChange}
        checkedLocations={checkedLocations}
        locationValues={locationValues}
      />
    </BulkSelectionLayout>
  );
}

export default NationalPlusUpBulkSelection;

function NationalPlusUpBulkSelectionForm({
  onSubmitBulkSelection,
  onChangeBulkSelection,
  checkedLocations,
  locationValues,
}: NationalPlusUpBulkSelectionFormProps) {
  const formik = useFormik<NationalPlusUpBulkValues>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const result = await onSubmitBulkSelection(values);
        if (result === true) {
          resetForm();
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { isSubmitting, values, setFieldValue } = formik;
  const printTypeOptions = useMemo(
    () =>
      getNationalPlusUpBulkPrintTypeOptions({
        checkedLocations,
        locationValues,
      }),
    [checkedLocations, locationValues]
  );
  const storeCopyConfig = useMemo(
    () =>
      getNationalPlusUpBulkStoreCopyConfig({
        checkedLocations,
        locationValues,
        printType: values.print_type,
      }),
    [checkedLocations, locationValues, values.print_type]
  );

  useEffect(() => {
    onChangeBulkSelection?.(values);
  }, [values, onChangeBulkSelection]);

  useEffect(() => {
    if (
      values.print_type &&
      !printTypeOptions.some((option) =>
        doRRDPrintTypesMatch(option.value, values.print_type)
      )
    ) {
      setFieldValue("print_type", "");
      setFieldValue("store_copy_qnt", "");
    }
  }, [printTypeOptions, setFieldValue, values.print_type]);

  const storeCopyError =
    values.store_copy && values.print_type
      ? validateNationalPlusUpStoreCopyQuantity(
          values.store_copy_qnt,
          values.store_copy,
          storeCopyConfig || undefined
        )
      : "";
  const activeStoreCopyConfig = storeCopyConfig || undefined;
  const isFixedStoreCopyQuantity = isRRDStoreCopyFixedQuantity(
    activeStoreCopyConfig
  );
  const fixedStoreCopyQuantity = getRRDStoreCopyDefaultQuantity(
    activeStoreCopyConfig
  );
  const validationErrors = validateNationalPlusUpBulkSelection({
    values,
    checkedLocations,
    locationValues,
  });

  const canApply = hasBulkSelection(values) && validationErrors.length === 0;

  useEffect(() => {
    if (
      values.store_copy &&
      isFixedStoreCopyQuantity &&
      values.store_copy_qnt !== fixedStoreCopyQuantity
    ) {
      setFieldValue("store_copy_qnt", fixedStoreCopyQuantity);
    }
  }, [
    fixedStoreCopyQuantity,
    isFixedStoreCopyQuantity,
    setFieldValue,
    values.store_copy,
    values.store_copy_qnt,
  ]);

  const handleDistributionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue("distribution", event.target.checked);
  };

  const handlePrintTypeChange = (newValue: string | null) => {
    setFieldValue("print_type", newValue || "");
    setFieldValue("store_copy_qnt", "");
  };

  const handleStoreCopyChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setFieldValue("store_copy", checked);
    setFieldValue("print_type", checked ? values.print_type : "");
    setFieldValue(
      "store_copy_qnt",
      checked ? fixedStoreCopyQuantity || values.store_copy_qnt || "" : ""
    );
  };

  const handleStoreCopyQntChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isFixedStoreCopyQuantity) {
      return;
    }
    const value = event.target.value;
    setFieldValue(
      "store_copy_qnt",
      value && !Number.isNaN(Number(value)) ? parseInt(value, 10) : ""
    );
  };

  const handleApply = async () => {
    await formik.submitForm();
  };

  return (
    <FormikProvider value={formik}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Typography variant="body2" gutterBottom fontWeight={600}>
            Plus-Up Distribution{" "}
            <NationalPlusUpToolTips type="estimated_distribution" />
          </Typography>
          <RpMuiSwitch
            name="distribution"
            checked={values.distribution}
            onChange={handleDistributionChange}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Typography variant="body2" gutterBottom fontWeight={600}>
            Add Store Copy <NationalPlusUpToolTips type="store_copy" />
          </Typography>
          <RpMuiSwitch
            name="store_copy"
            checked={values.store_copy}
            onChange={handleStoreCopyChange}
          />
        </Grid>

        {values.store_copy ? (
          <Grid size={{ xs: 12, lg: 3 }}>
            <Typography variant="body2" gutterBottom fontWeight={600}>
              Print Type
            </Typography>
            <MUICustomAutocompleteSelect
              options={printTypeOptions}
              selectProps={{
                value: values.print_type,
                onChange: handlePrintTypeChange,
              }}
              wrapperProps={{ className: "ml-0" }}
            />
          </Grid>
        ) : null}

        <Grid size={{ xs: 12, lg: 3 }}>
          <Typography variant="body2" gutterBottom fontWeight={600}>
            Store Copy Quantity{" "}
            <NationalPlusUpToolTips customContent="Store copy must be ordered in increments of 1,000 (minimum 1,000)." />
          </Typography>
          <RpNumberField
            fullWidth
            placeholder="0"
            size="small"
            disabled={!values.store_copy || isFixedStoreCopyQuantity}
            readOnly={isFixedStoreCopyQuantity}
            value={values.store_copy_qnt}
            onChange={handleStoreCopyQntChange}
            type="text"
            decimalScale={0}
            maxValue={activeStoreCopyConfig?.maxQuantity}
            helperText={storeCopyError || ""}
            error={!!storeCopyError}
          />
        </Grid>

        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: { xs: "center", lg: "flex-end" },
            alignItems: "start",
            mt: { xs: 0, lg: 3 },
          }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={handleApply}
            disabled={isSubmitting || !canApply}
            sx={{ width: { xs: "100%", lg: "auto" } }}
          >
            Apply to selected locations
          </Button>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Pro tip:</strong> if you want to make minor changes to
              individual locations, first make bulk selections and then edit
              individual locations.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </FormikProvider>
  );
}

function hasBulkSelection(values: Partial<NationalPlusUpBulkValues> = {}) {
  return Boolean(values.distribution || values.store_copy);
}
